import { useState } from "react";
import { loadEnglishDictionaries } from "../../loader/loadEnglishDictionaries";
import { DialogArea } from "./DialogArea";
import { Say } from "../widgets/Say";
import { PartsTable } from "./PartsTable";

/** essay about how to customize dialog that sounds more natural */
export function DialogEssay() {
  const [init, setInit] = useState(false);
  if (!init) {
    setInit(true);
    loadEnglishDictionaries();
  }

  return (<div className='page'>
    <h1>Writing Dialog</h1>

    <h2>Defaults</h2>

    <p>
      By default, the speech synthesizer adjusts its rhythm and intonation
      based on where the stress or tone appears in words, punctuation,
      and a general understanding of the contours of an English sentence.
    </p>

    <p>How it interprets punctuation falls into a few groups:</p>

    <ul>
      <li>
        <b>Short pause:</b> It adds a short pause for a comma or various types of dashes and brackets.
        <br />
        Examples: <Say>1, 2, 3</Say>, <Say>First part - second part</Say>,
        or <Say>I went (walked) home</Say>.
      </li>
      <li>
        <b>Medium pause:</b> It adds a slightly longer pause for a colon (:) or semicolon (;).
        <br />
        Example: <Say>This is what they brought: clothes, toothbrush, and a book.</Say>
      </li>
      <li>
        <b>End of sentence:</b> When it finds a period (.), it drops the intonation and adds a longer pause.
        <br />
        Example: <Say>"Sentence" at the start sounds different than at the end of the sentence.</Say>
      </li>
      <li>
        <b>Question mark:</b> When it finds a question mark (?), it adds a longer pause and raises the intonation.
        <br />
        Example: <Say>Is this a question?</Say>
      </li>
    </ul>


    <h2>Prosody Markdown (PMD)</h2>

    <p>
      An English speaker adjusts the prosody of the sentence (stress, rhythm and intonation)
      to emphasize concepts and change the meaning of the sentence.
      To allow you to adjust the prosody of a sentence, the system supports annotations
      called <i>prosody markdown</i> (PMD), which you can add to your text.
    </p>

    <p>You can change how a word is spoken by adding a symbol at the front of it:</p>

    <ul>
      <li><Say>*Emphasize</Say> by adding *, for example: <Say>that's the best</Say> versus <Say>that's the *best</Say>.</li>
      <li><Say>%De-emphasize</Say> by adding %, for example: <Say>I have some free time</Say> versus <Say>%I have %some free %time</Say>.</li>
      <li><Say>+Lengthen</Say> vowels by adding +, for example: <Say>extend the syllables</Say> versus <Say>extend the +syllables</Say></li>
    </ul>

    <p>
      You can affect an entire sentence if you start it with a backslash (\)
      followed by one of the above symbols.
    </p>

    <ul>
      <li><Say>\* emphasize every word</Say>
        <br />versus <Say>emphasize every word</Say></li>
      <li><Say>\% use a *star to emphasize, and a 'tick for 'normal stress</Say>
        <br />versus <Say>use a star to emphasize, and a tick for normal stress</Say></li>
      <li><Say>\+ lengthen the whole sentence</Say>
        <br />versus <Say>lengthen the whole sentence</Say></li>
    </ul>

    <p>
      In English, putting stress on different syllables can change the meaning of a word -
      for example <Say say="project.n">project (noun)</Say> versus <Say say="project.v">project (verb)</Say>.
      In contrast, some languages differentiate words using <i>tones</i>.
      In Vietnamese for example, you have
      <Say say="/mæ230˧/">ma ("ghost")</Say> with a medium tone,
      <Say say="/mæ230˥˩/">mà ("but")</Say> with a falling tone,
      <Say say="/mæ230˩˥/">má ("cheek", regional)</Say> with a rising tone, and
      <Say say="/mæ230˧˩˧/">mả ("tomb")</Say> mid dipping-rising tone.
      While English doesn't differentiate words by tone, it can use similar intonation to indicate additional meaning,
      such as <Say say="/oː˩˥/">oh (rising, "is that so?"")</Say> versus <Say say="/oː˥˩/">oh (falling,
        "ah, I see")</Say> versus <Say say="/oː˦˨˦/" pitch={70}>oh (dipping, whiny)</Say>
    </p>

    <p>You can add tones, or a series of tones, to a word with the following prefixes:</p>

    <ul>
      <li><Say>1|extra-low</Say></li>
      <li><Say>2|low</Say></li>
      <li><Say>3|medium</Say></li>
      <li><Say>4|high</Say></li>
      <li><Say>5|extra-high</Say></li>
      <li><Say>/|rising</Say></li>
      <li><Say>\|falling</Say></li>
      <li><Say>^|rise-and-fall</Say></li>
      <li><Say>~|fall-and-rise</Say></li>
    </ul>

    <p>
      Additionally, you can specify an exact pronunciation if you know the IPA
      you want to use by placing the symbols inside a pair of slashes (/).
      For example, <Say>English words mixed with /paˈlaβɾas e̞n e̞spaˈɲo̞l/</Say>.
      See <a href="?page=speech-essay">Picking Speech Phonemes</a> for more information.
    </p>


    <h2>Writing Dialog</h2>

    <p>
      You can also choose to hear multiple people speak, each with a different voice,
      maybe even using different accents or languages.
      These instructions are placed inside curly braces, with text inside single quotes.
    </p>
    <DialogArea dialogText={languageExample} rows={2} />

    <p>
      Alternately, you can associate various properties with a name,
      then put that name before sentences you want to hear spoken.
      Here's a simple example using different voices with different languages and accents:
    </p>
    <DialogArea dialogText={simpleExample} rows={5} />

    <p>
      You can also adjust volume, pitch, speed, and left/right speaker panning.
      Here's an example illustrating these settings
    </p>
    <DialogArea dialogText={optionsExample} rows={5} />

    <p>Here's a summary of the options:</p>

    <PartsTable />


    <h2>Using IPA</h2>

    <p>
      If you want more control, you can use IPA - the International Phonetic Alphabet - rather
      than have it translate from a language such as English or Spanish.
      See <a href="?page=speech-essay">Picking Speech Phonemes</a> for detail on
      the sounds and symbols, as well as to learn what the speech synthesizer supports.
    </p>

    <DialogArea dialogText={`{"language":"IPA"} ˈðɪs jusɨz ˈaɪ̯ˈpʰiˈeɪ̯`} rows={1} />

    <p>
      Vowels, diphthongs, rhotic combinations, and syllabic consonants are spoken with a default length:
      roughly 160ms for most vowels and 220ms for many diphthongs.
      You can change this length by adding a three-digit number representing the length in milliseconds
      immediately after the vowel (<b>a200</b>), diphthong (<b>aɪ̯250</b>),
      rhotic combination (<b>ɑɹ300</b>), or syllabic consonant (<b>n̩150</b>).
      &nbsp;<i>Note that adding vowel length in this way is not part of IPA.</i>
    </p>

    <DialogArea dialogText={`{"language":"IPA"} ˈkʰɐ130stm̩170 ˈvaʊ̯220ɫ̩130 lɛ160ŋkθs`} rows={1} />


    <h2>Playground</h2>
    <DialogArea dialogText={partsStr} cols={105} />
  </div>);
}

const languageExample =
  `{"language":"Spanish", "accent":"Spain"} Hablo en español
{"language":"English", "accent":"Cockney"} then switch to a Cockney accent
`;

const simpleExample =
  `{"name":"one", "voice":"martin", "language":"Spanish", "accent":"Spain"}
{"name":"two", "voice":"fiona", "language":"English", "accent":"Cockney"}

{one:}Estoy hablando español con acento de España.
{two:}I'm speaking English with a Cockney accent.
`;

const optionsExample =
  `{"name":"one", "volume":200, "pitch":80, "speed":200, "pan":-1}
{"name":"two", "volume":50, "pitch":30, "speed":120, "pan":1}

{one:}I'm speaking loud, high, and fast.
{two:}I'm speaking quietly, low, and slow.
`;

/*
const optionsMartin: PartOptions = { voice: 'martin', pan: -0.5 };
const optionsFiona: PartOptions = { voice: 'fiona', pan: 0.5 };
const parts: SpeakingPart[] = [
  { words: "one", options: optionsMartin },
  { words: "two", options: optionsFiona },
  { words: "uno dos tres", options: optionsMartin, language: { language: 'Spanish' } },
  { words: "cuatro cinco seis", options: optionsFiona, language: { language: 'Spanish' } },
  { words: "now I can have one character talk for awhile in *one accent", options: optionsMartin, language: { language: 'English', accent: 'Scottish' } },
  { words: "then switch to another character using a *different accent", options: optionsFiona, language: { language: 'English' } },
];
*/

const partsStr =
  `{"name":"fiona", "voice":"fiona", "pan": 0.7, "language": "English", "accent":"Scouse (Liverpool)"}
{"name":"martin", "voice":"martin", "pan": -0.7, "language": "English", "accent":"Western US"}
{"name":"betty", "voice":"betty", "pan": 0, "volume":50, "language": "Spanish", "accent":"Spain"}

{fiona:} OK class, we're about to start
{martin:} I think somebody is at the door
{betty:} ¿Es esta una clase de español?
{fiona:} Nope, sorry, wrong classroom. You want the one down the hall.
{betty:} Gracias`;
